import React from 'react';
import { Container, Row, Col } from 'react-bootstrap';
import Seo from './Seo';
import { essays, podcasts, speaking, coverage } from '../press/coverage';

const SITE_URL = 'https://qcoda.com';

// ============================================================
// Press — /company/press
// ============================================================
// Four conditional sections:
//   1. Essays & Articles     — always shown (seeded with HackerNoon + blog)
//   2. Podcasts              — shown only when podcasts[] is non-empty
//   3. Speaking & Community  — shown only when speaking[] is non-empty
//   4. In the News           — shown only when coverage[] is non-empty
//
// SEO: self-referential canonical, ItemList JSON-LD for all items,
// descriptive meta for AEO (answer engine / LLM indexing).

function ExternalIcon() {
    return (
        <svg
            width="12"
            height="12"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
            style={{ marginLeft: '0.3em', opacity: 0.5, flexShrink: 0 }}
        >
            <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
            <polyline points="15 3 21 3 21 9" />
            <line x1="10" y1="14" x2="21" y2="3" />
        </svg>
    );
}

function SourceBadge({ label }) {
    return (
        <span
            style={{
                display: 'inline-block',
                fontSize: '0.7rem',
                fontWeight: 700,
                letterSpacing: '0.07em',
                textTransform: 'uppercase',
                color: 'rgba(120,170,240,0.85)',
                background: 'rgba(120,170,240,0.08)',
                border: '1px solid rgba(120,170,240,0.18)',
                borderRadius: '4px',
                padding: '0.15em 0.55em',
                marginRight: '0.6em',
                verticalAlign: 'middle',
            }}
        >
            {label}
        </span>
    );
}

function PressRow({ item, isLast }) {
    const isExternal = !item.internal;
    return (
        <article
            style={{
                padding: '1.25rem 0',
                borderBottom: isLast ? 'none' : '1px solid rgba(255,255,255,0.06)',
            }}
        >
            <a
                href={item.href}
                target={isExternal ? '_blank' : undefined}
                rel={isExternal ? 'noopener noreferrer' : undefined}
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '1rem',
                    textDecoration: 'none',
                    color: 'inherit',
                }}
                className="press-row-link"
            >
                {/* Thumbnail */}
                {item.image && (
                    <div
                        style={{
                            flexShrink: 0,
                            width: '72px',
                            height: '72px',
                            borderRadius: '6px',
                            overflow: 'hidden',
                            border: '1px solid rgba(255,255,255,0.08)',
                            background: 'rgba(255,255,255,0.04)',
                        }}
                    >
                        <img
                            src={item.image}
                            alt={item.imageAlt || item.title}
                            loading="lazy"
                            style={{
                                width: '100%',
                                height: '100%',
                                objectFit: 'cover',
                                display: 'block',
                            }}
                        />
                    </div>
                )}

                {/* Text */}
                <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ marginBottom: '0.3rem' }}>
                        <SourceBadge label={item.source || item.outlet || item.show || item.venue} />
                        <time
                            dateTime={item.date}
                            style={{ fontSize: '0.8rem', color: 'rgba(255,255,255,0.35)' }}
                        >
                            {item.dateDisplay}
                        </time>
                    </div>
                    <h3
                        style={{
                            fontSize: '1rem',
                            fontWeight: 600,
                            color: 'rgba(255,255,255,0.92)',
                            marginBottom: '0.25rem',
                            lineHeight: 1.4,
                            display: 'flex',
                            alignItems: 'center',
                        }}
                    >
                        {item.title}
                        {isExternal && <ExternalIcon />}
                    </h3>
                    {item.description && (
                        <p
                            style={{
                                fontSize: '0.88rem',
                                color: 'rgba(255,255,255,0.45)',
                                margin: 0,
                                lineHeight: 1.6,
                                overflow: 'hidden',
                                display: '-webkit-box',
                                WebkitLineClamp: 2,
                                WebkitBoxOrient: 'vertical',
                            }}
                        >
                            {item.description}
                        </p>
                    )}
                </div>
            </a>
        </article>
    );
}

function SectionHeading({ eyebrow, title }) {
    return (
        <div style={{ marginBottom: '1.5rem' }}>
            <p
                style={{
                    fontSize: '0.7rem',
                    fontWeight: 700,
                    letterSpacing: '0.1em',
                    textTransform: 'uppercase',
                    color: 'rgba(255,255,255,0.35)',
                    marginBottom: '0.4rem',
                }}
            >
                {eyebrow}
            </p>
            <h2
                style={{
                    fontSize: '1.25rem',
                    fontWeight: 700,
                    color: 'rgba(255,255,255,0.9)',
                    margin: 0,
                }}
            >
                {title}
            </h2>
        </div>
    );
}

function Press() {
    // Build ItemList JSON-LD from all items across all sections
    const allItems = [
        ...essays.map((e) => ({ ...e, type: 'Article' })),
        ...podcasts.map((p) => ({ ...p, type: 'PodcastEpisode' })),
        ...speaking.map((s) => ({ ...s, type: 'Event' })),
        ...coverage.map((c) => ({ ...c, type: 'NewsArticle' })),
    ];

    const jsonLd = {
        '@context': 'https://schema.org',
        '@graph': [
            {
                '@type': 'ItemList',
                name: 'QCoda Press & Media Coverage',
                description:
                    'Articles, essays, podcast appearances, speaking engagements, and media coverage featuring QCoda and its founder Andrew Schwabe.',
                url: `${SITE_URL}/company/press`,
                numberOfItems: allItems.length,
                itemListElement: allItems.map((item, i) => ({
                    '@type': 'ListItem',
                    position: i + 1,
                    url: item.href,
                    name: item.title,
                })),
            },
            {
                '@type': 'Organization',
                name: 'QCoda',
                url: `${SITE_URL}/`,
                logo: `${SITE_URL}/qcoda_icon_white.png`,
                founder: {
                    '@type': 'Person',
                    name: 'Andrew Schwabe',
                    url: 'https://www.linkedin.com/in/aschwabe/',
                },
                sameAs: [
                    'https://x.com/qcoda_ai',
                    'https://www.linkedin.com/company/qcoda',
                    'https://hackernoon.com/u/aschwabe',
                ],
            },
        ],
    };

    return (
        <main>
            <Seo
                title="Press & Media — Qcoda | AI Engineering Governance"
                description="Articles, essays, podcast appearances, and media coverage featuring QCoda — the AI Engineering Governance platform for AI-assisted software development."
                path="/company/press"
                jsonLd={jsonLd}
                jsonLdId="press-jsonld"
            />

            {/* ===== HEADER ===== */}
            <section className="site-section-lg" style={{ paddingBottom: '2rem' }}>
                <Container>
                    <Row className="justify-content-center text-center">
                        <Col lg={8} xl={7}>
                            <span className="badge-ea mb-4 d-inline-block">Press & Media</span>
                            <h1 className="section-heading mb-3">
                                QCoda in the world
                            </h1>
                            <p className="section-subheading">
                                Articles, essays, and appearances from the team behind QCoda.
                                For press inquiries, reach us at{' '}
                                <a href="mailto:hello@qcoda.com" style={{ color: 'rgba(120,170,240,0.9)' }}>
                                    hello@qcoda.com
                                </a>
                                .
                            </p>
                        </Col>
                    </Row>
                </Container>
            </section>

            <hr className="section-divider" />

            {/* ===== MAIN CONTENT ===== */}
            <section className="site-section">
                <Container>
                    <Row className="justify-content-center">
                        <Col lg={8} xl={7}>

                            {/* --- Essays & Articles --- */}
                            {essays.length > 0 && (
                                <div style={{ marginBottom: '3.5rem' }}>
                                    <SectionHeading
                                        eyebrow="Essays & Articles"
                                        title="Written by Andrew Schwabe"
                                    />
                                    <div
                                        style={{
                                            borderTop: '1px solid rgba(255,255,255,0.06)',
                                        }}
                                    >
                                        {essays.map((item, i) => (
                                            <PressRow
                                                key={item.href}
                                                item={item}
                                                isLast={i === essays.length - 1}
                                            />
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* --- Podcasts & Appearances --- */}
                            {podcasts.length > 0 && (
                                <div style={{ marginBottom: '3.5rem' }}>
                                    <SectionHeading
                                        eyebrow="Podcasts & Appearances"
                                        title="Andrew Schwabe as a guest"
                                    />
                                    <div
                                        style={{
                                            borderTop: '1px solid rgba(255,255,255,0.06)',
                                        }}
                                    >
                                        {podcasts.map((item, i) => (
                                            <PressRow
                                                key={item.href}
                                                item={item}
                                                isLast={i === podcasts.length - 1}
                                            />
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* --- Speaking & Community --- */}
                            {speaking.length > 0 && (
                                <div style={{ marginBottom: '3.5rem' }}>
                                    <SectionHeading
                                        eyebrow="Speaking & Community"
                                        title="Keynotes, panels, and workshops"
                                    />
                                    <div
                                        style={{
                                            borderTop: '1px solid rgba(255,255,255,0.06)',
                                        }}
                                    >
                                        {speaking.map((item, i) => (
                                            <PressRow
                                                key={item.href}
                                                item={item}
                                                isLast={i === speaking.length - 1}
                                            />
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* --- In the News --- */}
                            {coverage.length > 0 && (
                                <div style={{ marginBottom: '3.5rem' }}>
                                    <SectionHeading
                                        eyebrow="In the News"
                                        title="Media coverage of QCoda"
                                    />
                                    <div
                                        style={{
                                            borderTop: '1px solid rgba(255,255,255,0.06)',
                                        }}
                                    >
                                        {coverage.map((item, i) => (
                                            <PressRow
                                                key={item.href}
                                                item={item}
                                                isLast={i === coverage.length - 1}
                                            />
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* --- Press contact --- */}
                            <div
                                style={{
                                    borderTop: '1px solid rgba(255,255,255,0.06)',
                                    paddingTop: '2rem',
                                }}
                            >
                                <p
                                    style={{
                                        fontSize: '0.88rem',
                                        color: 'rgba(255,255,255,0.4)',
                                        margin: 0,
                                        lineHeight: 1.7,
                                    }}
                                >
                                    Press inquiries, interview requests, and media kit:{' '}
                                    <a
                                        href="mailto:hello@qcoda.com"
                                        style={{ color: 'rgba(120,170,240,0.8)' }}
                                    >
                                        hello@qcoda.com
                                    </a>
                                </p>
                            </div>

                        </Col>
                    </Row>
                </Container>
            </section>
        </main>
    );
}

export default Press;
